import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';

const defaultCategory = {
  id: "",
  name: "",
  description: "",
  icon: "",
  active: true,
  addedDate: ""
};

export default function Categories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [category, setCategory] = useState<any>(defaultCategory);
  const [saving, setSaving] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [categoryActiveFilter, setCategoryActiveFilter] = useState("");
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesSnap = await getDocs(collection(db, "categories"));
        const categoriesData = categoriesSnap.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredCategories = categories.filter((cat: any) => {
    const matchesSearch = (cat.name || "").toLowerCase().includes(categorySearchTerm.toLowerCase()) ||
      (cat.description || "").toLowerCase().includes(categorySearchTerm.toLowerCase());
    const matchesActive = categoryActiveFilter === "" ||
      (categoryActiveFilter === "active" && cat.active === true) ||
      (categoryActiveFilter === "inactive" && cat.active === false);
    return matchesSearch && matchesActive;
  });

  const handleCategoryChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setCategory((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleIconFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIconFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const openCreateModal = (categoryToEdit?: any) => {
    if (categoryToEdit) {
      setEditingCategory(categoryToEdit);
      setCategory(categoryToEdit);
      setIconPreview(categoryToEdit.icon || "");
    } else {
      setEditingCategory(null);
      setCategory(defaultCategory);
      setIconPreview("");
    }
    setShowCreateModal(true);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setSaving(true);

    try {
      let iconUrl = category.icon;

      if (iconFile) {
        const iconRef = ref(storage, `categories/${Date.now()}_${iconFile.name}`);
        await uploadBytes(iconRef, iconFile);
        iconUrl = await getDownloadURL(iconRef);
      }

      const categoryData = {
        ...category,
        icon: iconUrl,
        addedDate: category.addedDate || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const { id, ...categoryDataToSave } = categoryData;

      if (editingCategory) {
        await setDoc(
          doc(db, "categories", editingCategory.id),
          categoryDataToSave,
          { merge: true }
        );
        setCategories((prev: any[]) => 
          prev.map(c => c.id === editingCategory.id ? { ...c, ...categoryData } : c)
        );
        alert("Category updated successfully!");
      } else {
        const categoryRef = await addDoc(collection(db, "categories"), categoryDataToSave);
        const newCategory = { ...categoryData, id: categoryRef.id };
        setCategories((prev: any) => [...prev, newCategory]);
        alert("Category created successfully!");
      }

      setShowCreateModal(false);
      setCategory(defaultCategory);
      setIconFile(null);
      setIconPreview("");
      setEditingCategory(null);
    } catch (error) {
      console.error("Error saving category:", error);
      alert("Error saving category. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Categories</h1>
        <button
          onClick={() => openCreateModal()}
          className="bg-[#00e3ff] text-black px-4 py-2 rounded hover:bg-[#00e3ff]/80"
        >
          + Add Category
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-4 gap-2">
        <input
          type="text"
          className="p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#00e3ff] col-span-2"
          placeholder="Search categories by name or description..."
          value={categorySearchTerm}
          onChange={(e) => setCategorySearchTerm(e.target.value)}
        />
        <select
          className="p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#00e3ff]"
          value={categoryActiveFilter}
          onChange={(e) => setCategoryActiveFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full border">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border text-center">Name</th>
              <th className="p-2 border text-center">Description</th>
              <th className="p-2 border text-center">Active</th>
              <th className="p-2 border text-center">Added Date</th>
              <th className="p-2 border text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.map((cat: any) => (
              <tr key={cat.id} className="text-center">
                <td className="p-2 border">{cat.name}</td>
                <td className="p-2 border">{cat.description || "-"}</td>
                <td className="p-2 border">{cat.active ? "Yes" : "No"}</td>
                <td className="p-2 border">
                  {cat.addedDate ? new Date(cat.addedDate).toLocaleDateString() : "-"}
                </td>
                <td className="p-2 border">
                  <button
                    className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                    onClick={() => openCreateModal(cat)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredCategories.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No categories found.
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                {editingCategory ? "Edit Category" : "Create Category"}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCategory(defaultCategory);
                  setIconFile(null);
                  setIconPreview("");
                  setEditingCategory(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={category.name}
                  onChange={handleCategoryChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  name="description"
                  value={category.description || ""}
                  onChange={handleCategoryChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Icon</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleIconFileChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
                {iconPreview && (
                  <img src={iconPreview} alt="Icon preview" className="mt-2 h-16 w-16 object-contain" />
                )}
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="active"
                    checked={category.active}
                    onChange={handleCategoryChange}
                    className="mr-2"
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCategory(defaultCategory);
                    setIconFile(null);
                    setIconPreview("");
                    setEditingCategory(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-[#00e3ff] text-black rounded hover:bg-[#00e3ff]/80 disabled:opacity-50"
                >
                  {saving ? "Saving..." : editingCategory ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

